/* eslint-disable no-console */
import Marked from 'marked';
import Prism from 'prismjs';
import Vue from 'vue';
import $service from '../util/service';
import DataTabs from '../components/core/DataTabs.vue';
import template from '../util/feature-gallery-codeview-template';

// The "Data Load Event" feature sample (feature-data-loaded-event) loads its chart data from a
// jsonbin.io URL that no longer exists (HTTP 410), so "Render Chart" ends in "No data to
// display". Until the sample is fixed at its source, run it with the same data - the sample's
// own `chartData` - served from a blob: URL created inside the sample page. The sample still
// loads by URL, so its loaded/error events behave exactly as before.
const DEAD_DATA_URL = 'https://api.jsonbin.io/b/5babd91b6d95da7b7a6cf39b/2';
// The sample's data says "Last week" while its x-axis is fixed dates (Jan 1 - Jan 7), so the
// subtitle was wrong the moment it was written and only gets worse. A fixed range stays true.
const FIXED_SUBCAPTION = 'Jan 1 - Jan 7';
// rawgit.com was shut down (its URLs now 404), which silently breaks samples that load a helper
// script from it, e.g. the drill-down sample's data table. jsDelivr serves the same GitHub
// file with a proper JavaScript content type (raw.githubusercontent.com does not, so browsers
// refuse to run scripts from it).
function withLiveScriptUrls(code = '') {
   return code.replace(
      /https?:\/\/rawgit\.com\/([^/\s"']+)\/([^/\s"']+)\/([^/\s"']+)\/([^\s"']+)/g,
      'https://cdn.jsdelivr.net/gh/$1/$2@$3/$4'
   );
}
// The same sample draws its chart only after a button click, into an empty container, so its page
// is just a button row at first and then grows by the chart's height - the frame would jump (and
// the chart flicker as the frame resizes under it). Reserve the chart's space from the start.
function withReservedChartSpace(cssCode = '', jsCode = '') {
   return jsCode.includes(DEAD_DATA_URL)
      ? `${cssCode}
#chart-container { min-height: 400px; }
`
      : cssCode;
}
function withLiveDataUrl(jsCode = '', chartData) {
   if (!chartData || !jsCode.includes(DEAD_DATA_URL)) return jsCode;
   const data = { ...chartData, chart: { ...chartData.chart, subcaption: FIXED_SUBCAPTION } };
   const blobUrl = `URL.createObjectURL(new Blob([${JSON.stringify(
      JSON.stringify(data)
   )}], { type: 'application/json' }))`;
   return [`"${DEAD_DATA_URL}"`, `'${DEAD_DATA_URL}'`].reduce(
      (code, quoted) => code.split(quoted).join(blobUrl),
      jsCode
   );
}

export default {
   methods: {
      // a global mixin used to convert mark down text to html
      markedRender: (markdown = '') =>
         Marked(markdown, {
            highlight(code, lang) {
               const paredHTML = Prism.highlight(
                  code,
                  Prism.languages[lang] || Prism.languages.markup
               );
               return paredHTML;
            },
            langPrefix: 'language-',
         }),

      isNumber: evt => {
         const charCode = evt.which ? evt.which : evt.keyCode;
         if (
            charCode > 31 &&
            (charCode < 48 || charCode > 57) &&
            charCode !== 46 &&
            charCode !== 43 &&
            charCode !== 16 &&
            charCode !== 32
         ) {
            evt.preventDefault();
            return false;
         }
         return true;
      },

      $toCamelCase(str) {
         return str.replace(/-([a-z])/g, g => g[1].toUpperCase());
      },

      // it decides, which type of reload required
      $reloadType(url) {
         const path = String(url || '');
         // /dev (docs) and /blog are root-relative, so they look internal to
         // the domain-pattern check below - but they're actually served by a
         // completely different backend behind the same nginx host. Vue
         // Router has no route for them, so router.push() just renders this
         // app's own 404 instead of ever making the real HTTP request that
         // would let nginx's path-based routing send it to the right place.
         if (/^\/(dev|blog)(\/|$)/.test(path)) {
            return true;
         }
         const pattern = new RegExp(
            '^(http://www.|https://www.|http://|https://)?[a-z0-9]+([-.]{1}[a-z0-9]+)*.[a-z]{2,5}(:[0-9]{1,5})?(/.*)?$'
         );
         return pattern.test(path);
      },

      // Callers already check $reloadType(url) before reaching here, so this
      // only has to pick same-tab vs new-tab. Everything replaces the
      // current tab (root-relative /dev and /blog, and external domains
      // like cart.fusioncharts.com alike) except GitHub, which is a
      // separate product/site the visitor is expected to come back from.
      $openReload(url) {
         const target = String(url || '');
         if (/^(https?:)?\/\/(www\.)?github\.com(\/|$)/i.test(target)) {
            window.open(target, '_blank', 'noopener');
         } else {
            window.location.href = target;
         }
      },

      // check for not empty
      isNotEmpty(value = '') {
         return value === '';
      },

      // label positions
      switchLabel(event) {
         if (event.target.value !== '') return true;
         const obj = event.target.previousElementSibling.classList;
         const { type } = event;
         if (type === 'focus' && !obj.contains('active')) {
            obj.add('active');
         } else if (type === 'blur' && obj.contains('active')) {
            obj.remove('active');
         }
         return true;
      },

      descriptionJoiner(arr, glue = ', ', intGlue = ',') {
         if (!Array.isArray(arr)) return arr;

         return arr.reduce((acc, item, idx) => {
            if (idx === 0) return item;

            const lastChar = acc.slice(-1);
            const firstChar = item.slice(0, 1);
            const isLastInt = !Number.isNaN(parseInt(lastChar, 10));
            const isFirstInt = !Number.isNaN(parseInt(firstChar, 10));

            let _acc = acc;
            if (isLastInt && isFirstInt) {
               _acc += intGlue;
            } else {
               _acc += glue;
            }

            _acc += item;
            return _acc;
         }, '');
      },

      async renderFusionCharts(
         el,
         {
            type,
            width = '100%',
            height = '100%',
            showCodeView = false,
            customHTML = false,
            objectHeight,
            objectWidth,
         } = {},
         callBackFunction
      ) {
         // Every call stamps the element. If the page re-renders (mounted + updated both call
         // this) while an earlier call is still waiting for its chart data, that earlier call
         // is stale: it must not append a second copy of the chart once its data arrives.
         const renderId = (el._fcRenderId || 0) + 1;
         el._fcRenderId = renderId;
         try {
            const dataSlugUrl = el.getAttribute('data-fs-slug');
            const loadAsPage = customHTML;
            const chartConfig = {
               type,
               width,
               height,
               dataFormat: 'json',
            };

            const contents = await $service.get(
               `https://s3.eu-central-1.amazonaws.com/fusion.store/${dataSlugUrl}-all.json`
            );

            // a newer render of this same element started while we were waiting - drop ours
            if (el._fcRenderId !== renderId) return;

            const chartContainer = document.createElement('div');
            const seoImgCon = el.getElementsByClassName('seo-image-container');

            chartConfig.dataSource = contents.data.chartData;

            el.append(chartContainer);

            if (loadAsPage) {
               chartContainer.className = 'chart-object-holder';

               const finalHTMLContent = template({
                  cssCode: withReservedChartSpace(contents.data.cssCode, contents.data.jsCode),
                  htmlCode: withLiveScriptUrls(contents.data.htmlCode),
                  jsCode: withLiveScriptUrls(
                     withLiveDataUrl(contents.data.jsCode, contents.data.chartData)
                  ),
               });

               const ObjectTag = document.createElement('object');
               chartContainer.appendChild(ObjectTag);
               ObjectTag.setAttribute('type', 'text/html');
               // The sample page reports its real height (see feature-gallery-codeview-template);
               // size the frame to it so there is no blank space, and keep following it as the
               // sample changes. objectHeight below is only the starting height until it reports.
               const onEmbedHeight = evt => {
                  if (!document.body.contains(ObjectTag)) {
                     window.removeEventListener('message', onEmbedHeight);
                  } else if (
                     evt.source === ObjectTag.contentWindow &&
                     evt.data &&
                     evt.data.fcEmbedHeight
                  ) {
                     ObjectTag.setAttribute('height', Math.min(evt.data.fcEmbedHeight, 4000));
                  }
               };
               window.addEventListener('message', onEmbedHeight);
               ObjectTag.setAttribute('width', '100%');
               objectHeight && ObjectTag.setAttribute('height', objectHeight + 75);
               objectWidth && ObjectTag.setAttribute('height', objectWidth);
               ObjectTag.setAttribute(
                  'data',
                  `data:text/html;charset=utf-8,${escape(finalHTMLContent)}`
               );
            } else {
               // on before render removing the seo image
               if (seoImgCon.length) {
                  chartConfig.events = {
                     beforeRender: () => {
                        seoImgCon[0].style.display = 'none';
                     },
                  };
               }

               chartContainer.className = 'chart-holder';
               const obj = new FusionCharts(chartConfig).render(chartContainer);
               chartContainer.setAttribute('data-chartId', obj.id);
            }

            if (showCodeView) {
               const properties = {
                  datasource: chartConfig.dataSource,
                  codepenOptions: {
                     cssCode: contents.data.cssCode,
                     htmlCode: contents.data.htmlCode,
                     jsCode: contents.data.jsCode,
                  },
                  chartType: type,
               };
               const DataTabsClass = Vue.extend(DataTabs);
               const instance = new DataTabsClass({
                  propsData: properties,
               });
               instance.$mount();
               const parentEl = chartContainer.parentNode;
               parentEl.insertBefore(instance.$el, chartContainer);
            }
         } catch (error) {
            console.warn('unable to render chart - ');
            console.log(error);
         } finally {
            callBackFunction && callBackFunction();
         }
      },

      unbindFusionCharts(el) {
         // Remove an Existing Chart
         const chrtId = el.getAttribute('data-chartid');

         if (chrtId) {
            const chartObj = FusionCharts(chrtId);
            if (typeof chartObj !== 'undefined') chartObj.dispose();
         }
      },

      $getUserSelectedFramework() {
         try {
            const { pathname } = window.location;
            let product = 'fcTechSelection';
            if (pathname.indexOf('fusionexport') > -1) {
               product = 'feTechSelection';
            } else if (pathname.indexOf('fusiontime') > -1) {
               product = 'ftTechSelection';
            }

            if (typeof window !== 'undefined' && window.localStorage) {
               let info = window.localStorage.getItem('userInfo');
               info = JSON.parse(info);
               return info ? info[product] : false;
            }

            return false;
         } catch (error) {
            console.warn(error);
            return false;
         }
      },

      $setUserSelectedFramework(param, prod) {
         try {
            const { pathname } = window.location;
            let product = 'fcTechSelection';
            if (prod === 'FusionExport' || pathname.indexOf('fusionexport') > -1) {
               product = 'feTechSelection';
            } else if (prod === 'FusionTime' || pathname.indexOf('fusiontime') > -1) {
               product = 'ftTechSelection';
            }
            let userInfo = window.localStorage.getItem('userInfo');
            userInfo = (userInfo && JSON.parse(userInfo)) || {};

            if (typeof window !== 'undefined' && window.localStorage) {
               const data = Object.assign(userInfo, { [product]: param });
               window.localStorage.setItem('userInfo', JSON.stringify(data));
            }
         } catch (error) {
            console.warn(error);
         }
      },

      $executeScripts(codeString = null) {
         try {
            const head = document.getElementsByTagName('head')[0] || document.documentElement;

            if (head && codeString && typeof codeString === 'string') {
               const script = document.createElement('script');
               script.type = 'text/javascript';
               script.appendChild(document.createTextNode(codeString));
               head.insertBefore(script, head.firstChild);
               head.removeChild(script);
               return true;
            }
            return false;
         } catch (error) {
            console.error('Facing issue with chart rendering', error);
            return false;
         }
      },

      $getCookie(k) {
         return ((document && document.cookie.match(`(^|; )${k}=([^;]*)`)) || 0)[2];
      },
   },

   computed: {
      // checking for is valid name
      missingName() {
         return this.form.name !== '';
      },

      // check for valid email
      isValidEmail() {
         const re = /(.+)@(.+){2,}\.(.+){2,}/;
         return !re.test(this.form.email.toLowerCase());
      },

      // check for valid phone Number
      wrongNumber() {
         return !(this.form.phone.length > 4);
      },
   },
   mounted() {},
   directives: {
      // to select country base on ip address
      getAutoCountryName: {
         bind: (el, binding, vnode) => {
            // API details:  https://dev.maxmind.com/geoip/geoip2/javascript/
            if (typeof geoip2 !== 'undefined') {
               geoip2.country(
                  location => {
                     if (vnode.context.form) {
                        vnode.context.form = {
                           ...vnode.context.form,
                           ...{ country: location.country && location.country.iso_code },
                           ...{ region: location.continent && location.continent.code },
                        };
                     }
                  },
                  () => {
                     console.log('GeopIp is not working in this domain.');
                  }
               );
            }
         },
      },

      fusioncharts: {
         inserted: (el, bindings, vn) => {
            vn.context.renderFusionCharts(el, bindings.value);
         },
         unbind: (el, bindings, vn) => {
            vn.context.unbindFusionCharts(el, bindings);
         },
      },

      lazyImage: {
         inserted(element) {
            if ('IntersectionObserver' in window) {
               const { src } = element.dataset;

               let intersected = false;
               const observer = new IntersectionObserver(
                  entries => {
                     const image = entries[0];
                     if (image.isIntersecting && !intersected) {
                        intersected = true;

                        // if (element.nodeName == "IMG") {
                        //    const imgE = document.createElement('img');
                        //    imgE.setAttribute('src', src);
                        //    imgE.className = `loaded-image ${  element.classList.value}`;
                        //    element.parentNode.insertBefore(imgE, element);
                        //    element.classList.add('hide-image');
                        // } else {
                        //    element.src = src;
                        // }
                        element.src = src;

                        observer.disconnect();
                     }
                  },
                  {
                     root: null,
                     rootMargin: '0px',
                     threshold: 0.1,
                  }
               );

               observer.observe(element);
            } else {
               const { src } = element.dataset;
               element.src = src;
            }
         },
      },
   },
};
